import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: 'What insurance plans do you accept?',
    answer:
      'We accept most major insurance plans including ODB (Ontario Drug Benefit), Green Shield, Sun Life, Manulife, Desjardins, Blue Cross, Great-West Life, and many more. Our team will process your claim in real time to minimize out-of-pocket costs.',
  },
  {
    question: 'Can I transfer my prescription from another pharmacy?',
    answer:
      'Yes, absolutely. Transferring your prescription to Thornhill Drug-Mart is simple. Just bring in your current prescription bottle or ask us to contact your previous pharmacy directly — we handle the transfer process for you.',
  },
  {
    question: 'Do you offer free prescription delivery?',
    answer:
      'Yes! We offer free home delivery for prescriptions throughout Thornhill, North York, Richmond Hill, and Vaughan. Simply call us or request delivery at the counter and we will arrange a convenient delivery time for you.',
  },
  {
    question: 'What is medication compounding and do I need it?',
    answer:
      "Medication compounding is the creation of customized medications tailored to a patient's specific needs — for example, a different dose, a liquid instead of a tablet, or a flavoured version for children. Your pharmacist can advise whether compounding is appropriate for your situation.",
  },
  {
    question: 'Do you provide immunizations and vaccines?',
    answer:
      'Yes. Our pharmacists are certified to administer flu shots, COVID-19 vaccines, shingles vaccines, travel vaccines, and more. Walk-ins are welcome — no appointment necessary for most immunizations.',
  },
  {
    question: 'How long does it take to fill a prescription?',
    answer:
      'Most prescriptions are filled within 15 minutes. For compounding, specialty medications, or first-time fills requiring insurance verification, it may take longer. We will give you an accurate wait time when you drop off your prescription.',
  },
];

export default function PharmacyFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <>
      {/* FAQ Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }}
      />

      <section aria-labelledby="faq-heading" className="mb-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-9"
        >
          <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-4">FAQ</p>
          <h2 id="faq-heading" className="text-3xl font-bold text-gray-900 leading-snug">
            Frequently asked questions
          </h2>
        </motion.div>

        <dl className="divide-y divide-gray-200 border-t border-b border-gray-200">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05, ease: 'easeOut' }}
            >
              <dt>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={openIndex === i}
                  className="w-full flex items-center justify-between py-5 text-left text-sm font-semibold text-gray-800 hover:text-teal-700 transition-colors cursor-pointer"
                >
                  <span>{faq.question}</span>
                  <span
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-6 text-gray-400"
                    aria-hidden="true"
                  >
                    <i className={`${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'} text-base transition-transform`}></i>
                  </span>
                </button>
              </dt>
              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <motion.dd
                    key="answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <p className="pb-5 text-sm text-gray-500 leading-relaxed max-w-2xl">
                      {faq.answer}
                    </p>
                  </motion.dd>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </dl>
      </section>
    </>
  );
}